import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Cookie, BarChart3, Users, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Air Purifier Calculator</title>
        <meta name="description" content="Privacy policy for Air Purifier Calculator. Learn how we collect, use, and protect your data." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          {/* Back link */}
          <Link to="/">
            <Button variant="ghost" className="mb-6 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Calculator
            </Button>
          </Link>

          <article className="prose prose-slate dark:prose-invert max-w-none">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-3xl font-bold m-0">Privacy Policy</h1>
            </div>

            <p className="text-muted-foreground">
              Last updated: February 1, 2026
            </p>

            <p>
              Air Purifier Calculator ("we", "our", or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website.
            </p>

            <section className="mt-8">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">Information We Collect</h2>
              </div>
              
              <h3 className="text-lg font-medium mt-4">Automatically Collected Information</h3>
              <p>When you visit our site, we automatically collect:</p>
              <ul>
                <li><strong>Usage data:</strong> Pages visited, time spent, scroll depth, and interactions with the calculator</li>
                <li><strong>Device information:</strong> Browser type, device type (mobile/desktop), screen size</li>
                <li><strong>Referral data:</strong> How you found us (search engine, social media, direct)</li>
              </ul>

              <h3 className="text-lg font-medium mt-4">Calculator Input Data</h3>
              <p>
                Room dimensions and air quality concerns you enter are processed locally in your browser. 
                We may collect anonymized, aggregated statistics about calculator usage (e.g., average room sizes) 
                to improve our recommendations.
              </p>
            </section>

            <section className="mt-8">
              <div className="flex items-center gap-2 mb-3">
                <Cookie className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">Cookies & Tracking</h2>
              </div>
              
              <p>We use the following services:</p>
              
              <h3 className="text-lg font-medium mt-4">Google Analytics 4</h3>
              <p>
                We use GA4 to understand how visitors use our site. Google Analytics collects information 
                such as how often users visit, what pages they view, and what other sites they used prior 
                to coming to our site.
              </p>
              <p>
                You can opt out of Google Analytics by installing the{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                  Google Analytics Opt-out Browser Add-on
                </a>.
              </p>

              <h3 className="text-lg font-medium mt-4">Session Storage</h3>
              <p>
                We use browser session storage to maintain your session ID for analytics purposes. 
                This data is automatically cleared when you close your browser.
              </p>
            </section>

            <section className="mt-8">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">Third-Party Services</h2>
              </div>

              <p>
                We may include links to third-party products and services in the future. 
                Any such partnerships will be clearly disclosed on this page.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-xl font-semibold">How We Use Your Information</h2>
              <ul>
                <li>To provide and improve our CADR calculator</li>
                <li>To understand how visitors use our site</li>
                <li>To personalize product recommendations</li>
                <li>To analyze trends and optimize user experience</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-xl font-semibold">Data Retention</h2>
              <p>
                Analytics data is retained according to Google Analytics default settings (26 months). 
                Session data is cleared when you close your browser.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-xl font-semibold">Your Rights (GDPR/CCPA)</h2>
              <p>Depending on your location, you may have the right to:</p>
              <ul>
                <li>Access the personal data we hold about you</li>
                <li>Request correction or deletion of your data</li>
                <li>Opt out of analytics tracking</li>
                <li>Request a copy of your data in a portable format</li>
              </ul>
              <p>
                <strong>California Residents:</strong> Under the CCPA, you have the right to know what personal 
                information we collect and to request its deletion. We do not sell personal information.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-xl font-semibold">Children's Privacy</h2>
              <p>
                Our site is not intended for children under 13. We do not knowingly collect personal 
                information from children.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-xl font-semibold">Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes 
                by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section className="mt-8">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-semibold m-0">Contact Us</h2>
              </div>
              <p>
                If you have any questions about this Privacy Policy, please contact us through our website.
              </p>
            </section>
          </article>
        </div>

        <Footer />
      </div>
    </>
  );
}
